import React, { useEffect, useState } from 'react';
import {
  Typography,
  CircularProgress,
  Grid,
  IconButton,
  Collapse,
  Box,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { CheckCircle, Cancel, ExpandMore, ExpandLess } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import proxy from '../../api/axios';

const ACCENT = '#137274';

const stripHtml = (html = '') =>
  String(html)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const PreviousChallenges = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const surface = isDark ? '#161b22' : '#ffffff';

  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(true);

  useEffect(() => {
    const fetchChallenges = async () => {
      try {
        const response = await proxy.get('/challenge/previous-daily-challenges');
        setChallenges(response.data?.previousChallenges || []);
      } catch (err) {
        setChallenges([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChallenges();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
        <CircularProgress size={22} sx={{ color: ACCENT }} />
      </Box>
    );
  }

  if (!challenges.length) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 3,
        p: { xs: 2, sm: 2.5 },
        borderRadius: 2.5,
        border: `1px solid ${border}`,
        bgcolor: surface,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: isCollapsed ? 0 : 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.02em' }}>
          Previous challenges
        </Typography>
        <IconButton
          size="small"
          onClick={() => setIsCollapsed((v) => !v)}
          aria-label={isCollapsed ? 'Expand' : 'Collapse'}
        >
          {isCollapsed ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      </Box>

      <Collapse in={!isCollapsed}>
        <Grid container spacing={1.5}>
          {challenges.map((challenge, index) => {
            if (!challenge.slug) return null;
            const preview = stripHtml(challenge.description || '').slice(0, 100);

            return (
              <Grid item xs={12} sm={6} md={4} key={`${challenge.slug}-${index}`}>
                <Box
                  component={Link}
                  to={`/problems/${challenge.slug}`}
                  sx={{
                    display: 'block',
                    textDecoration: 'none',
                    color: 'inherit',
                    p: 1.75,
                    height: '100%',
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                    bgcolor: isDark ? alpha('#fff', 0.02) : alpha(ACCENT, 0.02),
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: alpha(ACCENT, 0.45) },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                    }}
                  >
                    <Chip
                      size="small"
                      label={`Day ${challenge.day}`}
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        bgcolor: alpha(ACCENT, isDark ? 0.2 : 0.1),
                        color: ACCENT,
                      }}
                    />
                    <Chip
                      size="small"
                      icon={
                        challenge.isSolved ? (
                          <CheckCircle sx={{ fontSize: '14px !important' }} />
                        ) : (
                          <Cancel sx={{ fontSize: '14px !important' }} />
                        )
                      }
                      label={challenge.isSolved ? 'Solved' : 'Missed'}
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: challenge.isSolved
                          ? alpha('#1a7f37', isDark ? 0.25 : 0.1)
                          : alpha('#b42318', isDark ? 0.25 : 0.08),
                        color: challenge.isSolved
                          ? isDark
                            ? '#3fb950'
                            : '#1a7f37'
                          : isDark
                            ? '#ffa198'
                            : '#b42318',
                        '& .MuiChip-icon': { color: 'inherit' },
                      }}
                    />
                  </Box>

                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
                    {challenge.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: muted,
                      fontSize: '0.75rem',
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {preview}
                    {(challenge.description || '').length > 100 ? '…' : ''}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Collapse>
    </Box>
  );
};

export default PreviousChallenges;
